import axios from "axios";
import urls from "./Urls";
import { action, makeObservable, observable, runInAction } from "mobx";
import dictionary from "./Dictionary";
import imageStore from "./Images";
import { replacePlaceholders } from "../Utils/additionalInfo";

const addFullStop = (text) => (!/[,;:.!?]$/.test(text.trim())) ? text + '.' : text;

class AdditionalInfo {
    existsInfo = null;
    newInfo = null;
    expandedState = {}; // for Accordion state (images, sentences, definitions, stories, distractingForTrns, distractingForOriginal, distractingForDefinitionInEng, distractingForDefinitionInHeb, distractingForWordFamilies, sentencesWithMissing, sentencesWithLastMissing, opposites, synonyms, trnsAr, trnsRu)
    loading = {
        create: { sentence: false, definition: false, imageDescription: false, image: false, story: false, distractingForTrns: false, distractingForOriginal: false, distractingForDefinitionInEng: false, distractingForDefinitionInHeb: false, distractingForWordFamilies: false, sentencesWithMissing: false, sentencesWithLastMissing: false, opposites: false, synonyms: false, trnsAr: false, trnsRu: false },
        getExistsInfo: false, addExistsInfo: {}, updateExistsInfo: {}, deleteExistsInfo: {},
        addAllExistsInfo: {},
        aiSettings: false
    };
    snackbar = { open: false, message: "", severity: "error" };
    aiSettings = null;
    sectionsToShow = []; // for the sections to show in the UI (images, sentences, definitions, stories, distractingForTrns, distractingForOriginal, distractingForDefinitionInEng, distractingForDefinitionInHeb, distractingForWordFamilies, sentencesWithMissing, sentencesWithLastMissing, opposites, synonyms, trnsAr, trnsRu)

    // Constructor _____________________________________________________________
    constructor() {
        makeObservable(this, {
            existsInfo: observable,
            newInfo: observable,

            expandedState: observable,
            loading: observable,
            snackbar: observable,
            sectionsToShow: observable,

            setExpandedState: action,

            setSnackbar: action,
            clearSnackbar: action,

            getExistsInfo: action,
            addExistsInfo: action,
            updateExistsInfo: action,
            deleteExistsInfo: action,

            createWithAi: action,
            createWithData: action,

            // AI Settings
            aiSettings: observable,
            getAiSettings: action,

            setSelectedProvider: action,
            setSelectedPrompt: action,

            createPrompt: action,
            updatePrompt: action,
            deletePrompt: action
        });

        this.clearExistsInfo();
        this.clearNewInfo();
        this.expandedState = this.getInitialExpandedState();
        this.sectionsToShow = this.getInitialSectionsToShow();

        // Bind the clearSnackbar method to ensure it retains the correct "this" context when called.
        this.clearSnackbar = this.clearSnackbar.bind(this);
    }

    // Loading _____________________________________________________________
    setLoading_NewInfo(field, value) {
        this.loading.create[field] = value;
    }

    setLoading_getExistsInfo(value) {
        runInAction(() => {
        this.loading.getExistsInfo = value;
        });
    }

    setLoading_addExistsInfo(field, index, value) {
        if (!this.loading.addExistsInfo[field]) this.loading.addExistsInfo[field] = {};
        this.loading.addExistsInfo[field][index] = value;
    }

    setLoading_addAllExistsInfo(field, value) {
        this.loading.addAllExistsInfo[field] = value;
    }

    setLoading_updateExistsInfo(field, nested_id, value) {
        if (!this.loading.updateExistsInfo[field]) this.loading.updateExistsInfo[field] = {};
        this.loading.updateExistsInfo[field][nested_id] = value;
    }

    setLoading_deleteExistsInfo(field, nested_id, value) {
        if (!this.loading.deleteExistsInfo[field]) this.loading.deleteExistsInfo[field] = {};
        this.loading.deleteExistsInfo[field][nested_id] = value;
    }

    // Accordion _____________________________________________________________
    getInitialExpandedState() {
        const savedState = localStorage.getItem("accordionState");
        return savedState ? JSON.parse(savedState) : { images: false, sentences: false, definitions: false, stories: false, distractingForTrns: false, distractingForOriginal: false, distractingForDefinitionInEng: false, distractingForDefinitionInHeb: false, distractingForWordFamilies: false, sentencesWithMissing: false, sentencesWithLastMissing: false, opposites: false, synonyms: false, trnsAr: false, trnsRu: false };
    }

    // Update the Accordion state and save it to localStorage
    setExpandedState(newState) {
        this.expandedState = newState;
        localStorage.setItem("accordionState", JSON.stringify(newState));
    }

    // Snackbar _____________________________________________________________
    setSnackbar(message, severity = "error") {
        this.snackbar = { open: true, message, severity };
    }

    clearSnackbar() {
        this.snackbar = { open: false, message: "", severity: "" };
    }

    // Sections to show _______________________________________________________
    getInitialSectionsToShow() {
        const savedState = localStorage.getItem("sectionsToShow");
        return savedState ? JSON.parse(savedState) : ["images", "sentences", "definitions", "stories", "distractingForTrns", "distractingForOriginal", "distractingForDefinitionInEng", "distractingForDefinitionInHeb", "distractingForWordFamilies", "sentencesWithMissing", "sentencesWithLastMissing", "opposites", "synonyms", "trnsAr", "trnsRu"];
    }

    setSectionsToShow(sections) {
        this.sectionsToShow = sections;
        localStorage.setItem("sectionsToShow", JSON.stringify(sections));
    }

    // Exists Info _____________________________________________________________
    async getExistsInfo(additionalInfoId, dictionaryType, wordId) {
        try {
            this.setLoading_getExistsInfo(true);

            const response = await axios.get(`${urls.additionalInfo.get}${additionalInfoId}/${dictionaryType}/${wordId}`, {
                withCredentials: true
            });

            runInAction(() => {
                this.existsInfo = response.data;
                this.clearNewInfo();
            });
        } catch (error) {
            // this.setSnackbar(`Error getting additional information - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.setLoading_getExistsInfo(false);
        }
    }

    async addAllExistsInfo(field) {
        try {
            const fieldMapping = {
                distractingForTrns: "distrForTrns",
                distractingForOriginal: "distrForOriginal",
                distractingForDefinitionInEng: "distrForDefinitionInEng",
                distractingForDefinitionInHeb: "distrForDefinitionInHeb",
                distractingForWordFamilies: "distrForWordFamilies",
                sentencesWithMissing: "sentencesMissing",
                sentencesWithLastMissing: "sentencesLastMissing",
                opposites: "opposite",
                synonyms: "synonym"
            };

            const isArray = Object.keys(fieldMapping).includes(field);
            if (!isArray) return;

            this.setLoading_addAllExistsInfo(field, true);
            while (this.newInfo[field].length > 0) {
                const value = this.newInfo[field][0];
                const internalField = fieldMapping[field];

                await this.addExistsInfo({
                    fields: { [internalField]: value },
                    field,
                    index: 0
                });
            }
        } catch (error) {
            this.setSnackbar(`Error adding all additional information - ${error}`, "error");
        } finally {
            this.setLoading_addAllExistsInfo(field, false);
        }
    }

    async addExistsInfo({ fields = {}, field, index }) {
        if (field === "sentences" && fields.sentence) {
            fields.sentence = addFullStop(fields.sentence);
        }
        try {
            if (field) this.setLoading_addExistsInfo(field, index, true);

            const wordId = dictionary.current.value._id;
            const dictionaryType = dictionary.current.dictionaryType;

            // Image is a URL
            const imageUrl = typeof fields.image === "string" && fields.image.startsWith("http")
                ? fields.image
                : null;

            // New additional info
            let newAdditionalInfo = {
                word: { dictionaryType, _id: wordId },
                images: { fileName: imageUrl },
                sentences: { text: fields.sentence },
                definitions: { text: fields.definition },
                stories: { text: fields.story },
                distractingForTrns: { text: fields.distrForTrns },
                distractingForOriginal: { text: fields.distrForOriginal },
                distractingForDefinitionInEng: { text: fields.distrForDefinitionInEng },
                distractingForDefinitionInHeb: { text: fields.distrForDefinitionInHeb },
                distractingForWordFamilies: { text: fields.distrForWordFamilies },
                sentencesWithMissing: { text: fields.sentencesMissing },
                sentencesWithLastMissing: { text: fields.sentencesLastMissing },
                opposites: { text: fields.opposite },
                synonyms: { text: fields.synonym },
                trnsAr: { text: fields.trns_ar },
                trnsRu: { text: fields.trns_ru },
            };

            // Delete empty fields
            Object.keys(newAdditionalInfo).forEach(key => {
                if (
                    newAdditionalInfo[key] == null ||
                    (typeof newAdditionalInfo[key] === 'object' &&
                    Object.values(newAdditionalInfo[key])[0] == null)
                ) {
                    delete newAdditionalInfo[key];
                }
            });

            let response;
            // Image is a file
            if (fields.image instanceof File) {
                const formData = new FormData();
                formData.append("image", fields.image);
                formData.append("word", JSON.stringify({ dictionaryType, _id: wordId }));

                response = await axios.patch(urls.additionalInfo.add, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true
                });
            } else {
                response = await axios.patch(urls.additionalInfo.add, newAdditionalInfo, {
                    withCredentials: true
                });
            }

            const updatedAdditionalInfo = response.data.updatedAdditionalInfo;

            if (field === "images") await imageStore.updateImages(updatedAdditionalInfo._id);

            runInAction(() => {
                this.existsInfo = updatedAdditionalInfo;

                const isArray = [
                    "distractingForTrns",
                    "distractingForOriginal",
                    "distractingForDefinitionInEng",
                    "distractingForDefinitionInHeb",
                    "distractingForWordFamilies",
                    "sentencesWithMissing",
                    "sentencesWithLastMissing",
                    "opposites",
                    "synonyms",
                    "trnsAr",
                    "trnsRu"
                ].includes(field);

                const fieldToUpdate = isArray ? field : field?.slice(0, -1); // Remove the last character for singular form
                if (field) this.deleteNewInfo(fieldToUpdate, index);

                // Update the dictionary with the new additional info
                dictionary.updateAdditionalInfo();

                if (["trnsAr", "trnsRu"].includes(field)) {
                    if (field === "trnsAr")
                        dictionary.updateCurrentFieldLocally(field, fields.trns_ar);
                    else if (field === "trnsRu")
                        dictionary.updateCurrentFieldLocally(field, fields.trns_ru);
                }
            });
        } catch (error) {
            console.error("Error adding additional information:", error);
            this.setSnackbar(`Error adding additional information - ${error} - ${error?.response?.data}`, "error");
        } finally {
            if (field) this.setLoading_addExistsInfo(field, index, false);
        }
    }

    async updateExistsInfo(_id, field, nested_id, updateData) {
        if (field === "sentences" && updateData.text) {
            updateData.text = addFullStop(updateData.text);
        }
        try {
            // Set the loading state to the key of the first object in updateData
            this.setLoading_updateExistsInfo(field, nested_id, Object.keys(updateData)[0]); // e.g. "text", "default"

            const dictionaryType = dictionary.current.dictionaryType;
            const word_id = dictionary.current.value._id;

            const response = await axios.patch(urls.additionalInfo.update,
                { _id, field, nested_id, updateData, dictionaryType, word_id },
                { withCredentials: true }
            );

            if (field === "images") await imageStore.updateImages(_id);

            runInAction(() => {
                this.existsInfo = response.data;

                if (["trnsAr", "trnsRu"].includes(field))
                    dictionary.updateCurrentFieldLocally(field, updateData.text);
            });
        } catch (error) {
            this.setSnackbar(`Error updating additional information - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.setLoading_updateExistsInfo(field, nested_id, false);
        }
    }

    async deleteExistsInfo(_id, field, nested_id) {
        try {
            this.setLoading_deleteExistsInfo(field, nested_id, true);

            const dictionaryType = dictionary.current.dictionaryType;
            const word_id = dictionary.current.value._id;

            const response = await axios.delete(`${urls.additionalInfo.delete}${dictionaryType}/${_id}/${field}/${nested_id}/${word_id}`, {
                withCredentials: true
            });

            if (field === "images") await imageStore.updateImages(_id);

            runInAction(() => {
                const updatedAdditionalInfo = response.data;
                this.existsInfo = updatedAdditionalInfo;

                dictionary.updateAdditionalInfo();

                if (["trnsAr", "trnsRu"].includes(field))
                    dictionary.updateCurrentFieldLocally(field, null);
            });
        } catch (error) {
            this.setSnackbar(`Error deleting additional information - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.setLoading_deleteExistsInfo(field, nested_id, false);
        }
    }

    async clearExistsInfoField(_id, field) {
        try {
            this.setLoading_updateExistsInfo(field, "clear", true);

            const dictionaryType = dictionary.current.dictionaryType;
            const word_id = dictionary.current.value._id;

            const response = await axios.patch(urls.additionalInfo.clearField,
                { _id, field, dictionaryType, word_id },
                { withCredentials: true }
            );

            runInAction(() => {
                this.existsInfo = response.data;
                dictionary.updateAdditionalInfo();

                if (["trnsAr", "trnsRu"].includes(field))
                    dictionary.updateCurrentFieldLocally(field, []);
            });
        } catch (error) {
            this.setSnackbar(`Error clearing field - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.setLoading_updateExistsInfo(field, "clear", false);
        }
    }

    // New Info _____________________________________________________________
    async createWithAi(type, prompt = null) {
        try {
            // loading
            if (this.loading.create[type]) return;
            this.setLoading_NewInfo(type, true);

            // currentWord
            const currentWord = dictionary.getOne(dictionary.current.value._id);

            // prompt
            if (!prompt) {
                // Get the selected prompt for the type
                // console.log(toJS(this.aiSettings.prompts.options[type]));
                prompt = this.aiSettings.text.prompts.options[type]
                    .find(prompt => prompt._id === this.aiSettings.text.prompts.selected[type]).prompt;

                // Replace placeholders in the prompt with values from the current word
                prompt = replacePlaceholders(prompt, currentWord);
            }

            // request
            const response = await axios.post(urls.ai.create, {
                type,
                prompt,
                provider: this.aiSettings.text.providers.selected,
                model: this.aiSettings.text.models[this.aiSettings.text.providers.selected].selected,
                temperature: this.aiSettings.text.temperature
            }, {
                withCredentials: true
            });
            const result = response.data.result;

            let noData = false;
            runInAction(() => {
                const isArray = [
                    "distractingForTrns",
                    "distractingForOriginal",
                    "distractingForDefinitionInEng",
                    "distractingForDefinitionInHeb",
                    "distractingForWordFamilies",
                    "sentencesWithMissing",
                    "sentencesWithLastMissing",
                    "opposites",
                    "synonyms"
                ].includes(type);

                if (isArray) {
                    const match = result.match(/\[[^\]]*\]/);
                    const arr = match ? JSON.parse(match[0]) : [];
                    if (arr.length === 0) { noData = true; }
                    for (const text of arr)
                        this.newInfo[type].push(text);
                } else {
                    this.newInfo[type].push(result);
                }
            });

            // snackbar
            const model = response.data.model;
            const elapsedTime = response.data.elapsedTime;
            if (type === "image") {
                this.setSnackbar(`התמונה נוצרה בהצלחה! מודל: ${model}, זמן: ${elapsedTime} שניות.`, "success");
            }
            else {
                let temperature = response.data.temperature;
                if (["o1", "o3-mini"].includes(model)) temperature = "אינו נתמך בדגם זה";
                const { input, output, total } = response.data.tokens;
                this.setSnackbar(`
                    הטקסט נוצר בהצלחה! מודל: ${model}, טמפרטורה: ${temperature}, זמן: ${elapsedTime} שניות.
                    טוקנים שאלה: ${input}, טוקנים תשובה: ${output}, סך הכל: ${total} טוקנים.
                   `, "success");
                if (noData) {
                    setTimeout(() => {
                        this.setSnackbar(
                            `יש בעיה בפרומפט שכתבת.
                            הוסף לפרומפט את ההוראה הבאה:
                            "את ה-${type} הנוספים תשים בתוך תחביר של מערך, כדי שאוכל לשלוף אותם באמצעות JSON.parse"`,
                            "warning");
                    }, 3000);
                }
            }
        } catch (error) {
            this.setSnackbar(
                `Error creating ${type} - ${error} - ${error?.response?.data}`
                    .replace(/&quot;/g, '"').replace(/&amp;/g, '&')
                , "error");
        } finally {
            this.setLoading_NewInfo(type, false);
        }
    }

    async createWithData(type, data) {
        try {
            // save data
            let noData = false;
            runInAction(() => {
                if (Array.isArray(data) && data.length > 0) {
                    for (const text of data) {
                        this.newInfo[type].push(text);
                    }
                } else {
                    noData = true;
                }
            });

            // snackbar
            if (noData) {
                this.setSnackbar(
                    `שמת לב? המידע שהעברת ל-${type} ריק או לא בפורמט מתאים.`,
                    "warning"
                );
            } else {
                // this.setSnackbar(`המידע נוסף בהצלחה עבור ${type}.`, "success");
            }
        } catch (error) {
            this.setSnackbar(
                `Error adding data for ${type} - ${error}`,
                "error"
            );
        }
    }

    deleteAllNewInfo(field) {
        if (field === "storie") field = "story";
        this.newInfo[field] = [];
    }

    deleteNewInfo(field, index) {
        if (field === "storie") field = "story";
        this.newInfo[field].splice(index, 1);
    }

    editNewInfo(field, index, value) {
        this.newInfo[field][index] = value;
    }

    // Clear _____________________________________________________________
    clearExistsInfo() {
        this.existsInfo = {
            _id: null,
            images: [
                // {
                //     fileName: null,
                //     _id: null
                // }
            ],
            sentences: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            definitions: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            stories: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            distractingForTrns: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            distractingForOriginal: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            distractingForDefinitionInEng: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            distractingForDefinitionInHeb: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            distractingForWordFamilies: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            sentencesWithMissing: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            sentencesWithLastMissing: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            opposites: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            synonyms: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            trnsAr: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            trnsRu: [
                // {
                //     text: null,
                //     _id: null
                // }
            ],
            createdAt: null
        };
    }
    clearNewInfo() {
        this.newInfo = {
            sentence: [],
            definition: [],
            imageDescription: [],
            image: [],
            story: [],
            distractingForTrns: [],
            distractingForOriginal: [],
            distractingForDefinitionInEng: [],
            distractingForDefinitionInHeb: [],
            distractingForWordFamilies: [],
            sentencesWithMissing: [],
            sentencesWithLastMissing: [],
            opposites: [],
            synonyms: [],
            trnsAr: [],
            trnsRu: []
        };
    }

    // AI Settings ___________________________________________________________
    async getAiSettings() {
        try {
            const response = await axios.get(urls.ai.getSettings, {
                withCredentials: true
            });
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            // this.setSnackbar(`Error getting AI settings - ${error} - ${error?.response?.data}`, "error");
        }
    }

    async setSelectedProvider(type, provider) {
        try {
            this.loading.aiSettings = true;
            const response = await axios.patch(`${urls.ai.setSelectedProvider}${type}/${provider}`, {}, {
                withCredentials: true
            });
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error setting selected provider - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }

    async setSelectedModel(type, model) {
        try {
            this.loading.aiSettings = true;
            const provider = this.aiSettings.text.providers.selected;
            const response = await axios.patch(`${urls.ai.setSelectedModel}${type}/${provider}/${model}`, {}, {
                withCredentials: true
            });
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error setting selected model - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }

    async setTemperature(type, temperature) {
        try {
            this.loading.aiSettings = true;
            const response = await axios.patch(`${urls.ai.setTemperature}${type}/${temperature}`, {}, {
                withCredentials: true
            });
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error setting temperature - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }

    async setSelectedPrompt(type, category, promptId) {
        try {
            this.loading.aiSettings = true;
            const response = await axios.patch(`${urls.ai.setSelectedPrompt}${type}/${category}/${promptId}`, {}, {
                withCredentials: true
            });
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error setting selected prompt - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }

    async createPrompt(type, category, prompt, name) {
        try {
            this.loading.aiSettings = true;
            const response = await axios.post(urls.ai.createPrompt,
                { type, category, newPrompt: prompt, name },
                { withCredentials: true }
            );
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error creating prompt - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }

    async updatePrompt(type, category, promptId, prompt, name) {
        try {
            this.loading.aiSettings = true;
            const response = await axios.patch(urls.ai.updatePrompt,
                { type, category, promptId, newPrompt: prompt, name },
                { withCredentials: true }
            );
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error updating prompt - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }

    async deletePrompt(type, category, promptId) {
        try {
            this.loading.aiSettings = true;
            const response = await axios.delete(`${urls.ai.deletePrompt}${type}/${category}/${promptId}`, {
                withCredentials: true
            });
            runInAction(() => {
                this.aiSettings = response.data;
            });
        } catch (error) {
            this.setSnackbar(`Error deleting prompt - ${error} - ${error?.response?.data}`, "error");
        } finally {
            this.loading.aiSettings = false;
        }
    }
}

const additionalInfo = new AdditionalInfo();
export default additionalInfo;
